// AGENT-A5: a single row in the category list.
//
// Layout: [▾ caret?] [checkbox] [label] [count badge]
// The whole row is clickable (toggles selection). The caret, when present
// (i.e. parents.length > 0), expands inline parent-product checkboxes.

import { useState } from "react";
import type { FilterCategory } from "../types";
import { useFilterStore } from "../state/filterStore";
import { useUiStore } from "../state/uiStore";
import { TruncatedLabel } from "./TruncatedLabel";

export interface CategoryItemProps {
  category: FilterCategory;
}

function formatCount(n: number): string {
  return new Intl.NumberFormat("en-US").format(n);
}

export function CategoryItem({ category }: CategoryItemProps) {
  const selected = useFilterStore((s) => s.categories.has(category.name));
  const toggleCategory = useFilterStore((s) => s.toggleCategory);

  const parentSelected = useFilterStore((s) => s.parentProducts);
  const toggleParent = useFilterStore((s) => s.toggleParent);

  const partNumberSelected = useFilterStore((s) => s.partNumbers);
  const togglePartNumber = useFilterStore((s) => s.togglePartNumber);

  const expanded = useUiStore((s) => s.expandedCategories.has(category.name));
  const toggleExpanded = useUiStore((s) => s.toggleCategoryExpanded);

  // Third-level expansion (parent → part numbers). Lives as component-local
  // state because there's no UI store key for it yet; one Set per category
  // item is plenty since the sidebar mounts at most ~190 cards.
  const [expandedParents, setExpandedParents] = useState<Set<string>>(() => new Set());
  const toggleParentExpanded = (pname: string): void => {
    setExpandedParents((prev) => {
      const next = new Set(prev);
      if (next.has(pname)) next.delete(pname);
      else next.add(pname);
      return next;
    });
  };

  const hasParents = (category.parents?.length ?? 0) > 0;

  return (
    <li
      className={`dash-cat-item${selected ? " is-selected" : ""}`}
      role="presentation"
    >
      <button
        type="button"
        className="dash-cat-item-row"
        onClick={() => toggleCategory(category.name)}
        aria-pressed={selected}
      >
        {hasParents ? (
          <span
            className={`dash-cat-caret${expanded ? " is-open" : ""}`}
            onClick={(e) => {
              e.stopPropagation();
              toggleExpanded(category.name);
            }}
            role="button"
            tabIndex={0}
            aria-label={expanded ? "Collapse" : "Expand"}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                e.stopPropagation();
                toggleExpanded(category.name);
              }
            }}
          >
            ▾
          </span>
        ) : (
          <span className="dash-cat-caret dash-cat-caret--placeholder" aria-hidden />
        )}
        <span
          className="dash-cat-checkbox"
          role="checkbox"
          aria-checked={selected}
        >
          {selected ? <span aria-hidden>✓</span> : null}
        </span>
        <TruncatedLabel className="dash-cat-label" text={category.name} />
        <span className="dash-cat-count dash-num">{formatCount(category.count)}</span>
      </button>

      {hasParents && expanded ? (
        <ul className="dash-cat-children" aria-label={`${category.name} parent products`}>
          {category.parents!
            .filter((p) => {
              // NaN-chip-fix: drop rows with missing / blank / "nan" labels
              // so the sidebar never renders an empty row with a NaN chip.
              const name = typeof p?.name === "string" ? p.name.trim() : "";
              if (!name) return false;
              const lower = name.toLowerCase();
              return lower !== "nan" && lower !== "none" && lower !== "<na>";
            })
            .map((p) => {
              const psel = parentSelected.has(p.name);
              const count = typeof p.count === "number" && Number.isFinite(p.count) ? p.count : 0;
              const parts = Array.isArray(p.partNumbers) ? p.partNumbers : [];
              const hasParts = parts.length > 0;
              const partsOpen = expandedParents.has(p.name);
              return (
                <li key={p.name} className={`dash-cat-child${psel ? " is-selected" : ""}`}>
                  <button
                    type="button"
                    className="dash-cat-child-row"
                    onClick={() => toggleParent(p.name)}
                    aria-pressed={psel}
                  >
                    {hasParts ? (
                      <span
                        className={`dash-cat-caret${partsOpen ? " is-open" : ""}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleParentExpanded(p.name);
                        }}
                        role="button"
                        tabIndex={0}
                        aria-label={partsOpen ? "Collapse part numbers" : "Expand part numbers"}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            e.stopPropagation();
                            toggleParentExpanded(p.name);
                          }
                        }}
                      >
                        ▾
                      </span>
                    ) : (
                      <span className="dash-cat-caret dash-cat-caret--placeholder" aria-hidden />
                    )}
                    <span
                      className="dash-cat-checkbox"
                      role="checkbox"
                      aria-checked={psel}
                    >
                      {psel ? <span aria-hidden>✓</span> : null}
                    </span>
                    <TruncatedLabel className="dash-cat-label" text={p.name} />
                    {count > 0 ? (
                      <span className="dash-cat-count dash-num">{formatCount(count)}</span>
                    ) : null}
                  </button>
                  {hasParts && partsOpen ? (
                    <ul
                      className="dash-cat-grandchildren"
                      aria-label={`${p.name} part numbers`}
                    >
                      {parts.map((pn) => {
                        const pncount =
                          typeof pn.count === "number" && Number.isFinite(pn.count)
                            ? pn.count
                            : 0;
                        const pnsel = partNumberSelected.has(pn.name);
                        return (
                          <li
                            key={pn.name}
                            className={`dash-cat-grandchild${pnsel ? " is-selected" : ""}`}
                          >
                            <button
                              type="button"
                              className="dash-cat-grandchild-row"
                              onClick={() => togglePartNumber(pn.name)}
                              aria-pressed={pnsel}
                            >
                              <span className="dash-cat-caret-spacer" aria-hidden />
                              <span
                                className="dash-cat-checkbox"
                                role="checkbox"
                                aria-checked={pnsel}
                              >
                                {pnsel ? <span aria-hidden>✓</span> : null}
                              </span>
                              <span className="dash-cat-pn-label" title={pn.name}>
                                {pn.name}
                              </span>
                              {pncount > 0 ? (
                                <span className="dash-cat-count dash-num">
                                  {formatCount(pncount)}
                                </span>
                              ) : null}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  ) : null}
                </li>
              );
            })}
        </ul>
      ) : null}
    </li>
  );
}
